import { useState, useMemo } from 'react';
import {
  Search,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Filter,
  ListOrdered,
} from 'lucide-react';
import { COMMITMENT_REGISTRY } from '../../../data/oia/commitmentRegistry';
import type {
  Commitment,
  CommitmentPriority,
  SystemBuildStatus,
  CommitmentCategory,
} from '../../../types/oia';

// ── Category Display Labels ──
const CATEGORY_LABELS: Record<CommitmentCategory, string> = {
  data_rights: 'Data Rights',
  consent_management: 'Consent Management',
  sla_enforcement: 'SLA Enforcement',
  notification: 'Notification',
  financial: 'Financial',
  compliance_documentation: 'Compliance Documentation',
  safety: 'Safety',
  entity_integrity: 'Entity Integrity',
  regulatory_specific: 'Regulatory Specific',
};

// ── Filters ──
type PriorityFilter = CommitmentPriority | 'all';
type StatusFilter = SystemBuildStatus | 'all';
type CategoryFilter = CommitmentCategory | 'all';

export function PromiseKeeperDashboard() {
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [p0QueueMode, setP0QueueMode] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // ── Derive stats from the registry ──
  const stats = useMemo(() => {
    const total = COMMITMENT_REGISTRY.length;
    const built = COMMITMENT_REGISTRY.filter((c) => c.systemStatus === 'built').length;
    const partial = COMMITMENT_REGISTRY.filter((c) => c.systemStatus === 'partial').length;
    const missing = COMMITMENT_REGISTRY.filter((c) => c.systemStatus === 'missing').length;
    const outdated = COMMITMENT_REGISTRY.filter((c) => c.systemStatus === 'outdated').length;
    const p0Total = COMMITMENT_REGISTRY.filter((c) => c.priority === 'P0').length;
    const p0Missing = COMMITMENT_REGISTRY.filter(
      (c) => c.priority === 'P0' && c.systemStatus === 'missing'
    ).length;
    const p0Partial = COMMITMENT_REGISTRY.filter(
      (c) => c.priority === 'P0' && c.systemStatus === 'partial'
    ).length;
    const p1Total = COMMITMENT_REGISTRY.filter((c) => c.priority === 'P1').length;
    const p2Total = COMMITMENT_REGISTRY.filter((c) => c.priority === 'P2').length;

    return { total, built, partial, missing, outdated, p0Total, p0Missing, p0Partial, p1Total, p2Total };
  }, []);

  // ── Extract unique categories ──
  const categories = useMemo(() => {
    const cats = new Set<CommitmentCategory>();
    COMMITMENT_REGISTRY.forEach((c) => cats.add(c.category));
    return Array.from(cats).sort();
  }, []);

  // ── Filter commitments ──
  const filteredCommitments = useMemo(() => {
    let result: Commitment[] = [...COMMITMENT_REGISTRY];

    // P0 Queue mode overrides filters
    if (p0QueueMode) {
      result = result.filter(
        (c) => c.priority === 'P0' && (c.systemStatus === 'missing' || c.systemStatus === 'partial')
      );
      // Sort by ID for work-in-order
      result.sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
      return result;
    }

    // Priority filter
    if (priorityFilter !== 'all') {
      result = result.filter((c) => c.priority === priorityFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter((c) => c.systemStatus === statusFilter);
    }

    // Category filter
    if (categoryFilter !== 'all') {
      result = result.filter((c) => c.category === categoryFilter);
    }

    // Search
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.id.toLowerCase().includes(q) ||
          c.commitment.toLowerCase().includes(q) ||
          c.specifics.toLowerCase().includes(q) ||
          c.category.toLowerCase().includes(q) ||
          (c.owner && c.owner.toLowerCase().includes(q))
      );
    }

    return result;
  }, [priorityFilter, statusFilter, categoryFilter, searchQuery, p0QueueMode]);

  // ── Toggle expand ──
  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // ── Enter/Exit P0 Queue Mode ──
  const handleP0QueueToggle = () => {
    if (!p0QueueMode) {
      // Entering queue mode — reset other filters
      setPriorityFilter('all');
      setStatusFilter('all');
      setCategoryFilter('all');
      setSearchQuery('');
    }
    setP0QueueMode(!p0QueueMode);
  };

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display text-text-primary">Promise Keeper</h1>
          <p className="text-sm text-text-secondary mt-1">
            {stats.total} patient-facing commitments from the Terms of Service
          </p>
        </div>
      </div>

      {/* ── Summary Bar ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Total" value={stats.total} color="text-text-primary" bg="bg-bg-elevated" />
        <StatCard label="Built" value={stats.built} color="text-optimal" bg="bg-emerald-50" />
        <StatCard label="Partial" value={stats.partial} color="text-suboptimal" bg="bg-amber-50" />
        <StatCard label="Missing" value={stats.missing + stats.outdated} color="text-low" bg="bg-red-50" />
        <StatCard label="P0 Total" value={stats.p0Total} color="text-text-primary" bg="bg-bg-elevated" />
        <StatCard
          label="P0 Not Built"
          value={stats.p0Missing + stats.p0Partial}
          color="text-low"
          bg="bg-red-50"
          alert={stats.p0Missing + stats.p0Partial > 0}
        />
      </div>

      {/* ── P0 Queue Toggle ── */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleP0QueueToggle}
          className={`flex items-center gap-2 px-4 py-2.5 rounded text-sm font-medium transition-colors ${
            p0QueueMode
              ? 'bg-accent text-white'
              : 'bg-bg-card border border-bg-tertiary text-text-primary hover:bg-bg-elevated'
          }`}
        >
          <ListOrdered size={16} />
          {p0QueueMode ? 'Exit P0 Queue' : 'P0 Work Queue'}
          {!p0QueueMode && (
            <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-red-100 text-red-700 rounded">
              {stats.p0Missing + stats.p0Partial}
            </span>
          )}
        </button>
        {p0QueueMode && (
          <p className="text-sm text-text-secondary">
            Winter Protocol work queue — P0 commitments not yet built, sorted by ID. Work in order.
          </p>
        )}
      </div>

      {/* ── Filters (hidden in P0 Queue mode) ── */}
      {!p0QueueMode && (
        <div className="bg-bg-card border border-bg-tertiary rounded p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-text-muted" />
            <span className="text-sm font-medium text-text-secondary">Filters</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Priority */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Priority</label>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as PriorityFilter)}
                className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="all">All Priorities</option>
                <option value="P0">P0 — Critical</option>
                <option value="P1">P1 — Important</option>
                <option value="P2">P2 — Deferred</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Build Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
                className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="all">All Statuses</option>
                <option value="missing">Missing</option>
                <option value="partial">Partial</option>
                <option value="built">Built</option>
                <option value="outdated">Outdated</option>
              </select>
            </div>

            {/* Category */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Category</label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as CategoryFilter)}
                className="w-full px-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
              >
                <option value="all">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {CATEGORY_LABELS[cat] || cat}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-xs font-medium text-text-muted mb-1">Search</label>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="ID, text, category..."
                  className="w-full pl-8 pr-3 py-2 text-sm bg-bg-elevated border border-bg-tertiary rounded text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Results Count ── */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          Showing {filteredCommitments.length} of {stats.total} commitments
        </p>
        {(priorityFilter !== 'all' || statusFilter !== 'all' || categoryFilter !== 'all' || searchQuery) && !p0QueueMode && (
          <button
            onClick={() => {
              setPriorityFilter('all');
              setStatusFilter('all');
              setCategoryFilter('all');
              setSearchQuery('');
            }}
            className="text-sm text-accent hover:text-accent-light transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ── Commitment Table ── */}
      <div className="bg-bg-card border border-bg-tertiary rounded overflow-hidden">
        {/* Table Header */}
        <div className="hidden md:grid md:grid-cols-[80px_1fr_80px_90px_140px_80px_40px] gap-3 px-4 py-3 bg-[#18181B] text-white text-xs font-medium uppercase tracking-wide">
          <div>ID</div>
          <div>Commitment</div>
          <div>Priority</div>
          <div>Status</div>
          <div>Category</div>
          <div>Owner</div>
          <div></div>
        </div>

        {/* Table Body */}
        {filteredCommitments.length === 0 ? (
          <div className="px-4 py-12 text-center text-text-muted text-sm">
            No commitments match the current filters.
          </div>
        ) : (
          <div>
            {filteredCommitments.map((commitment, idx) => (
              <CommitmentRow
                key={commitment.id}
                commitment={commitment}
                isExpanded={expandedIds.has(commitment.id)}
                onToggle={() => toggleExpand(commitment.id)}
                isOdd={idx % 2 === 1}
                queuePosition={p0QueueMode ? idx + 1 : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Priority Breakdown (bottom) ── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <PriorityBreakdownCard
          priority="P0"
          total={stats.p0Total}
          missing={stats.p0Missing}
          partial={stats.p0Partial}
          built={stats.p0Total - stats.p0Missing - stats.p0Partial}
        />
        <PriorityBreakdownCard
          priority="P1"
          total={stats.p1Total}
          missing={COMMITMENT_REGISTRY.filter((c) => c.priority === 'P1' && c.systemStatus === 'missing').length}
          partial={COMMITMENT_REGISTRY.filter((c) => c.priority === 'P1' && c.systemStatus === 'partial').length}
          built={COMMITMENT_REGISTRY.filter((c) => c.priority === 'P1' && c.systemStatus === 'built').length}
        />
        <PriorityBreakdownCard
          priority="P2"
          total={stats.p2Total}
          missing={COMMITMENT_REGISTRY.filter((c) => c.priority === 'P2' && c.systemStatus === 'missing').length}
          partial={COMMITMENT_REGISTRY.filter((c) => c.priority === 'P2' && c.systemStatus === 'partial').length}
          built={COMMITMENT_REGISTRY.filter((c) => c.priority === 'P2' && c.systemStatus === 'built').length}
        />
      </div>
    </div>
  );
}

// ── Stat Card ──
function StatCard({
  label,
  value,
  color,
  bg,
  alert,
}: {
  label: string;
  value: number;
  color: string;
  bg: string;
  alert?: boolean;
}) {
  return (
    <div className={`${bg} border border-bg-tertiary rounded p-4 ${alert ? 'ring-2 ring-red-300' : ''}`}>
      <p className="text-xs font-medium text-text-muted uppercase tracking-wide">{label}</p>
      <p className={`text-2xl font-semibold font-mono mt-1 ${color}`}>{value}</p>
      {alert && (
        <div className="flex items-center gap-1 mt-1">
          <AlertTriangle size={12} className="text-red-500" />
          <span className="text-xs text-red-600 font-medium">Action required</span>
        </div>
      )}
    </div>
  );
}

// ── Commitment Row ──
function CommitmentRow({
  commitment,
  isExpanded,
  onToggle,
  isOdd,
  queuePosition,
}: {
  commitment: Commitment;
  isExpanded: boolean;
  onToggle: () => void;
  isOdd: boolean;
  queuePosition?: number;
}) {
  return (
    <div className={isOdd ? 'bg-[#F8FAFC]' : 'bg-white'}>
      {/* Main Row */}
      <button
        onClick={onToggle}
        className="w-full text-left grid grid-cols-1 md:grid-cols-[80px_1fr_80px_90px_140px_80px_40px] gap-2 md:gap-3 px-4 py-3 hover:bg-bg-elevated/60 transition-colors items-start"
      >
        {/* ID */}
        <div className="flex items-center gap-2">
          {queuePosition !== undefined && (
            <span className="text-xs font-mono text-text-muted w-5 text-right flex-shrink-0">
              {queuePosition}.
            </span>
          )}
          <span className="text-sm font-mono font-medium text-text-primary">{commitment.id}</span>
        </div>

        {/* Commitment Text */}
        <div className="text-sm text-text-primary leading-snug">
          <span className="md:hidden font-medium text-text-muted text-xs block mb-0.5">Commitment:</span>
          {commitment.commitment}
        </div>

        {/* Priority Badge */}
        <div>
          <span className="md:hidden font-medium text-text-muted text-xs mr-1">Priority:</span>
          <PriorityBadge priority={commitment.priority} />
        </div>

        {/* Status Badge */}
        <div>
          <span className="md:hidden font-medium text-text-muted text-xs mr-1">Status:</span>
          <StatusBadge status={commitment.systemStatus} />
        </div>

        {/* Category */}
        <div className="text-xs text-text-secondary">
          <span className="md:hidden font-medium text-text-muted mr-1">Category:</span>
          {CATEGORY_LABELS[commitment.category] || commitment.category}
        </div>

        {/* Owner */}
        <div className="text-xs text-text-secondary capitalize">
          <span className="md:hidden font-medium text-text-muted mr-1">Owner:</span>
          {commitment.owner}
        </div>

        {/* Expand Icon */}
        <div className="hidden md:flex justify-center">
          {isExpanded ? (
            <ChevronUp size={16} className="text-text-muted" />
          ) : (
            <ChevronDown size={16} className="text-text-muted" />
          )}
        </div>
      </button>

      {/* Expanded Detail */}
      {isExpanded && (
        <div className="px-4 pb-4 md:pl-[96px] space-y-4 border-t border-bg-tertiary">
          <div className="pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Specifics */}
            <div>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Specifics</h4>
              <p className="text-sm text-text-primary leading-relaxed">{commitment.specifics}</p>
            </div>

            {/* SLA */}
            {commitment.sla && (
              <div>
                <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">SLA</h4>
                <div className="flex items-center gap-1.5">
                  <Clock size={14} className="text-text-muted" />
                  <p className="text-sm text-text-primary font-medium">{commitment.sla}</p>
                </div>
              </div>
            )}

            {/* Type */}
            <div>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">Type</h4>
              <p className="text-sm text-text-primary capitalize">{commitment.type}</p>
            </div>

            {/* Scope Activities */}
            {commitment.scopeActivities.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-1">
                  Scope Activities
                </h4>
                <div className="flex flex-wrap gap-1">
                  {commitment.scopeActivities.map((sa) => (
                    <span key={sa} className="px-2 py-0.5 text-xs bg-bg-elevated border border-bg-tertiary rounded font-mono">
                      {sa}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Verification */}
          <div>
            <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">
              Verification (Belt & Suspenders)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-bg-elevated border border-bg-tertiary rounded p-3">
                <p className="text-xs font-semibold text-text-muted mb-1">Belt (Primary)</p>
                <p className="text-sm text-text-primary">{commitment.verification.belt}</p>
              </div>
              <div className="bg-bg-elevated border border-bg-tertiary rounded p-3">
                <p className="text-xs font-semibold text-text-muted mb-1">Suspenders (Redundant)</p>
                <p className="text-sm text-text-primary">{commitment.verification.suspenders}</p>
              </div>
            </div>
          </div>

          {/* System Gaps */}
          {commitment.systemGaps.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">System Gaps</h4>
              <ul className="space-y-1">
                {commitment.systemGaps.map((gap, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                    <XCircle size={14} className="mt-0.5 flex-shrink-0" />
                    {gap}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Document References */}
          {commitment.documents.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">
                Document References
              </h4>
              <div className="space-y-2">
                {commitment.documents.map((doc, i) => (
                  <div key={i} className="bg-bg-elevated border border-bg-tertiary rounded p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-text-muted">{doc.templateId}</span>
                      <span className="text-xs text-text-muted">{doc.section}</span>
                    </div>
                    <p className="text-sm text-text-secondary italic">"{doc.language}"</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quality Gate Keywords */}
          {commitment.qualityGateKeywords.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wide mb-2">
                Quality Gate Keywords
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {commitment.qualityGateKeywords.map((kw) => (
                  <span
                    key={kw}
                    className="px-2 py-0.5 text-xs bg-accent-soft text-accent border border-accent/20 rounded"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Priority Badge ──
function PriorityBadge({ priority }: { priority: CommitmentPriority }) {
  const styles: Record<CommitmentPriority, string> = {
    P0: 'bg-red-100 text-red-800 font-bold',
    P1: 'bg-orange-100 text-orange-800 font-medium',
    P2: 'bg-gray-100 text-gray-600 font-medium',
  };
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded ${styles[priority]}`}>
      {priority}
    </span>
  );
}

// ── Status Badge ──
function StatusBadge({ status }: { status: SystemBuildStatus }) {
  const config: Record<SystemBuildStatus, { label: string; className: string; Icon: typeof CheckCircle2 }> = {
    built: { label: 'Built', className: 'bg-emerald-100 text-emerald-800', Icon: CheckCircle2 },
    partial: { label: 'Partial', className: 'bg-amber-100 text-amber-800', Icon: Clock },
    missing: { label: 'Missing', className: 'bg-red-100 text-red-800', Icon: XCircle },
    outdated: { label: 'Outdated', className: 'bg-red-100 text-red-700', Icon: AlertTriangle },
  };

  const { label, className, Icon } = config[status];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded ${className}`}>
      <Icon size={12} />
      {label}
    </span>
  );
}

// ── Priority Breakdown Card ──
function PriorityBreakdownCard({
  priority,
  total,
  missing,
  partial,
  built,
}: {
  priority: string;
  total: number;
  missing: number;
  partial: number;
  built: number;
}) {
  const pctBuilt = total > 0 ? Math.round((built / total) * 100) : 0;
  const pctPartial = total > 0 ? Math.round((partial / total) * 100) : 0;
  const pctMissing = total > 0 ? Math.round((missing / total) * 100) : 0;

  const priorityColors: Record<string, string> = {
    P0: 'border-red-300',
    P1: 'border-orange-300',
    P2: 'border-gray-300',
  };

  return (
    <div className={`bg-bg-card border-2 ${priorityColors[priority] || 'border-bg-tertiary'} rounded p-4`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-text-primary">{priority} Commitments</h3>
        <span className="text-lg font-mono font-semibold text-text-primary">{total}</span>
      </div>

      {/* Progress bar */}
      <div className="h-2.5 bg-bg-elevated rounded-full overflow-hidden flex mb-3">
        {pctBuilt > 0 && (
          <div className="bg-emerald-500 transition-all" style={{ width: `${pctBuilt}%` }} />
        )}
        {pctPartial > 0 && (
          <div className="bg-amber-400 transition-all" style={{ width: `${pctPartial}%` }} />
        )}
        {pctMissing > 0 && (
          <div className="bg-red-400 transition-all" style={{ width: `${pctMissing}%` }} />
        )}
      </div>

      <div className="grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-mono font-semibold text-emerald-600">{built}</p>
          <p className="text-xs text-text-muted">Built</p>
        </div>
        <div>
          <p className="text-lg font-mono font-semibold text-amber-600">{partial}</p>
          <p className="text-xs text-text-muted">Partial</p>
        </div>
        <div>
          <p className="text-lg font-mono font-semibold text-red-600">{missing}</p>
          <p className="text-xs text-text-muted">Missing</p>
        </div>
      </div>
    </div>
  );
}
